import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Reveal from '@/components/Reveal';
import Img from '@/components/Img';
import Seo from '@/components/Seo';
import Faq from '@/components/Faq';
import PromoModal from '@/components/PromoModal';
import { MENS_PAGE } from '@/constants/testIds';
import { MENS_CATEGORIES } from '@/constants/site';
import { MENS_FAQS } from '@/constants/faqs';

const HERO_IMG = '/assets/m-suits-pair.jpg';

export default function MensBespoke() {
  return (
    <div data-testid={MENS_PAGE.root} className="bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))]">
      <PromoModal />
      <Seo
        title="Men's Bespoke Tailoring — Suits, Sherwanis, Shirts | Dubai"
        description="Bombay Bespoke — three decades of master tailoring in Dubai. Bespoke suits, tuxedos, sherwanis, kurtas, bandhgalas, shirts and trousers, cut to your own pattern by Mr. Hardeep Singh Gill in Al Karama."
        canonical="https://bombaybespokefashion.com/mens-bespoke"
      />
      <section className="relative overflow-hidden bb-grain" data-testid={MENS_PAGE.hero}>
        <div className="relative z-10 bb-container pt-14 md:pt-24 pb-12 md:pb-20 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">Bombay Bespoke · Men’s Atelier</div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="bb-display text-[58px] sm:text-[80px] lg:text-[112px] mt-5 leading-[0.92]">
                Master tailoring,
                <br />
                <span className="bb-italic text-[hsl(var(--bb-gold))]">measured</span> to you.
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-7 max-w-xl text-base md:text-lg text-[hsl(var(--bb-text))]/70 leading-relaxed">
                A pattern drafted only for you. Cloth selected from the world’s finest
                mills. Three decades of practice, in the hands of Mr. Hardeep Singh Gill —
                applied to suits, shirts, and the occasion garments that matter most.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden border border-[hsl(var(--bb-text))]/10">
              <Img src={HERO_IMG} alt="Bombay Bespoke suit" imgClassName="w-full h-full object-cover" />
              <span className="absolute left-4 bottom-4 px-3 py-2 bg-[hsl(var(--bb-charcoal))]/85 text-[10px] tracking-[0.24em] uppercase text-[hsl(var(--bb-gold))]">
                Bespoke · From measure
              </span>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="bb-container pb-24 md:pb-32">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <Reveal>
            <h2 className="bb-display text-4xl md:text-5xl">The Wardrobe</h2>
          </Reveal>
          <Reveal delay={120}>
            <span className="bb-eyebrow text-[hsl(var(--bb-gold))]">Five pillars of bespoke</span>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-5 md:gap-7">
          {MENS_CATEGORIES.map((cat, i) => {
            // 3 + 2 layout: row 1 = 4+4+4, row 2 = 6+6
            const span =
              i < 3
                ? 'col-span-12 sm:col-span-6 md:col-span-4'
                : 'col-span-12 sm:col-span-6';
            const aspect = i < 3 ? 'aspect-[3/4]' : 'aspect-[4/5]';
            return (
              <Reveal key={cat.slug} delay={i * 80} className={span}>
                <div
                  data-testid={MENS_PAGE.categoryCard(cat.slug)}
                  className="block relative overflow-hidden"
                >
                  <div className={`${aspect} relative overflow-hidden bb-image-hover border border-[hsl(var(--bb-text))]/10`}>
                    <Img src={cat.image} alt={cat.title} imgClassName="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                    {!cat.available && (
                      <span className="absolute top-4 right-4 px-3 py-1.5 bg-[hsl(var(--bb-charcoal))]/95 border border-[hsl(var(--bb-gold))]/40 text-[hsl(var(--bb-gold))] text-[10px] tracking-[0.24em] uppercase">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <div className="absolute left-0 right-0 bottom-0 p-5 md:p-6">
                    <div className="bb-display text-2xl md:text-3xl text-[hsl(var(--bb-text))]">{cat.title}</div>
                    <div className="mt-2 text-xs md:text-sm text-[hsl(var(--bb-text))]/75 max-w-xs">{cat.blurb}</div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200} className="mt-16 text-center">
          <div className="bb-display text-3xl md:text-4xl">
            Schedule <span className="bb-italic text-[hsl(var(--bb-gold))]">a fitting.</span>
          </div>
          <Link
            to="/book-appointment"
            data-testid={MENS_PAGE.bookCta}
            className="mt-8 inline-flex items-center gap-3 px-7 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.24em] text-[11px] font-semibold hover:bg-[hsl(var(--bb-text))] transition-colors duration-500"
          >
            Book an appointment
            <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>

      <section className="bg-[hsl(var(--bb-ivory))] border-t border-[hsl(var(--bb-text))]/10">
        <Faq
          eyebrow="Bombay Bespoke · FAQs"
          heading="The bespoke process,"
          italicWord="answered."
          items={MENS_FAQS}
          tone="gold"
        />
      </section>
    </div>
  );
}